import {
  NormalizedTrainingJob,
  TrainingForm,
  TrainingJobFromResponse,
  TrainingMethod,
  TrainingStatus
} from "@/types/training";
import { toDuration } from "./date";

export const normalizeJob = (job: TrainingJobFromResponse): NormalizedTrainingJob => {
  return {
    id: job.id,
    method: job.ft_type as TrainingMethod,
    hfDataset: job.dataset_hf,
    hfModel: job.base_model_hf,
    userId: job.user_id,
    exportedHfRepo: job.exported_hf_repo,
    status: job.status.unified_status as TrainingStatus,
    remainingTimeHours: job.status.remaining_time_hours,
    progress: job.status.progress,
    createdAt: job.created_at,
    endTime: job.end_time,
    endedAt: job.job_finished_at
  };
};

/**
 * Extracts the repository ID from a Hugging Face URL.
 * e.g., "https://huggingface.co/google/gemma-3n-E4B-it" -> "google/gemma-3n-E4B-it"
 *
 * @param {string} url The Hugging Face URL to parse.
 * @returns {string|null} The repository ID or null if the URL is invalid.
 */
export function extractRepoIdFromUrl(url: string) {
  try {
    const { pathname } = new URL(url);
    // Remove leading/trailing slashes and split
    const pathParts = pathname.replace(/^\/+|\/+$/g, "").split("/");
    // For URLs like /datasets/fka/awesome-chatgpt-prompts or /fka/awesome-chatgpt-prompts
    // Find the last two segments (org/user and repo name)
    if (pathParts.length >= 2) {
      return `${pathParts[pathParts.length - 2]}/${pathParts[pathParts.length - 1]}`;
    }
    return url;
  } catch (error) {
    return url;
  }
}

export const formatStatus = (status: TrainingStatus | string): string =>
  status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const getCreateTrainingRequestBody = (values: TrainingForm) => {
  return {
    base_model_hf: extractRepoIdFromUrl(values.model),
    dataset_hf: extractRepoIdFromUrl(values.dataset).split("/").pop(),
    ft_type: values.method,
    hf_token: values.hfToken?.trim() || undefined
  };
};

export const getRunTime = (createdAt: string, endedAt?: string | null): string => {
  const start = new Date(createdAt);
  const end = endedAt ? new Date(endedAt) : new Date();
  const diff = end.getTime() - start.getTime();
  const runTime = toDuration(diff) || "-";
  return runTime;
};

export const isJobFinished = (job: NormalizedTrainingJob): boolean => {
  return (
    job.status === "completed" ||
    job.status === "failed" ||
    job.status === "deploy_failed" ||
    job.status === "training_failed"
  );
};

export const isJobFailed = (job: NormalizedTrainingJob) =>
  !["completed", "training", "deploying"].includes(job.status);

export const normalizeBaseModel = (model: {
  display_name: string;
  hf_link: string;
}): { value: string; label: string } => {
  return {
    value: model.hf_link,
    label: model.display_name
  };
};

export const addRequestToGoogleSheet = async (data: {
  name: string;
  userId: string;
  model: string;
  dataset: string;
  method: string;
  intent?: string;
  contactInfo?: string;
}): Promise<boolean> => {
  try {
    const body: {
      username: string;
      userId: string;
      model: string;
      dataset: string;
      method: string;
      createdAt: string;
      intent?: string;
      contactInfo?: string;
    } = {
      username: data.name,
      userId: data.userId,
      method: data.method,
      model: data.model,
      dataset: data.dataset,
      createdAt: new Date().toISOString()
    };

    if (data.intent) {
      body.intent = data.intent;
    }
    if (data.contactInfo) {
      body.contactInfo = data.contactInfo;
    }

    const response = await fetch("/api/submit-training-request", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    });
    console.log("Response status:", response);
    const result = await response.json();
    console.log("Request added to Google Sheet successfully:", result);
    return true;
  } catch (error) {
    console.error("Failed to add request to Google Sheet:", error);
    return false;
  }
};
